import {useState} from 'react';
import {Link} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {Film, MessageSquareWarning, X} from 'lucide-react';
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
  buttonVariants
} from '@kroll-resolver-crisp/ui-components-react';
import {CopyUrlButton} from '@/components/shared/CopyableUrl';
import {type QaReviewItem} from '@/api/qaReview';
import {type RejectedSubmission} from '@/api/rejectedSubmissions';
import {
  useQaMyFeedback,
  useAcknowledgeQaFeedback,
  useQcMyFeedback,
  useAcknowledgeQcFeedback
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {riskVariant} from '@/lib/status';
import {SensitiveMedia} from '@/components/shared/SensitiveMedia';

// Dashboard real estate is precious - beyond this many alerts we collapse the rest behind a "+N more"
// button that opens the full list in a dialog, rather than letting the widget grow unboundedly.
const MAX_VISIBLE_CARDS = 3;

// Both review processes feed this widget: Weekly QA false positives (post-delivery) and QC rejections
// (pre-delivery). Their ids are distinct UUIDs, so one dismissed set serves both.
type FeedbackEntry =
  | {source: 'qa'; id: string; reviewedAt: string | null; item: QaReviewItem}
  | {source: 'qc'; id: string; reviewedAt: string | null; item: RejectedSubmission};

export function QaFeedbackWidget() {
  const qa = useQaMyFeedback();
  const qc = useQcMyFeedback();
  const acknowledgeQa = useAcknowledgeQaFeedback();
  const acknowledgeQc = useAcknowledgeQcFeedback();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showAll, setShowAll] = useState(false);

  const items: FeedbackEntry[] = [
    ...(qa.data ?? []).map((item) => ({
      source: 'qa' as const,
      id: item.id,
      reviewedAt: item.reviewed_at,
      item
    })),
    ...(qc.data ?? []).map((item) => ({
      source: 'qc' as const,
      id: item.id,
      reviewedAt: item.reviewed_at,
      item
    }))
  ].sort((a, b) => (b.reviewedAt ?? '').localeCompare(a.reviewedAt ?? ''));
  if ((qa.isLoading || qc.isLoading) && items.length === 0) {
    return null;
  }

  const visible = items.filter((item) => !dismissedIds.has(item.id));
  if (visible.length === 0) {
    return null;
  }

  const shown = visible.slice(0, MAX_VISIBLE_CARDS);
  const remaining = visible.length - shown.length;

  function handleDismiss(entry: FeedbackEntry) {
    const {id} = entry;
    setDismissedIds((previous) => new Set(previous).add(id));
    setErrors((previous) =>
      Object.fromEntries(Object.entries(previous).filter(([key]) => key !== id))
    );

    const acknowledge = entry.source === 'qa' ? acknowledgeQa : acknowledgeQc;
    acknowledge.mutate(id, {
      onError(error) {
        // Un-hide the card rather than leaving the UI lying about what actually got dismissed.
        setDismissedIds((previous) => {
          const next = new Set(previous);
          next.delete(id);
          return next;
        });
        setErrors((previous) => ({
          ...previous,
          [id]: getApiErrorMessage(error, 'Failed to dismiss feedback.')
        }));
      }
    });
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-medium">Rejected submissions</h3>
        {/* A styled Link rather than Button render={<Link>}: Base UI would stamp role="button" on it. */}
        <Link
          to="/rejected-submissions"
          className={buttonVariants({variant: 'link', size: 'sm', className: 'px-0'})}>
          View all feedback
        </Link>
      </div>

      <div className="space-y-2">
        {shown.map((entry) => (
          <FeedbackAlert
            key={entry.id}
            entry={entry}
            error={errors[entry.id]}
            onDismiss={() => {
              handleDismiss(entry);
            }}
          />
        ))}
      </div>

      {remaining > 0 ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="mt-1 px-0"
          onClick={() => {
            setShowAll(true);
          }}>
          +{remaining} more {remaining === 1 ? 'item' : 'items'} of feedback waiting for review
        </Button>
      ) : null}

      <Dialog open={showAll} onOpenChange={setShowAll}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Rejected submissions</DialogTitle>
            <DialogDescription>
              All {visible.length} {visible.length === 1 ? 'item' : 'items'} of feedback waiting for
              review.
            </DialogDescription>
          </DialogHeader>

          {/* The body is the one part that scrolls, so the title stays put while a long list is
              read; the alerts' own buttons give it a tab stop. */}
          <DialogBody>
            {visible.map((entry) => (
              <FeedbackAlert
                key={entry.id}
                entry={entry}
                error={errors[entry.id]}
                onDismiss={() => {
                  handleDismiss(entry);
                }}
              />
            ))}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FeedbackAlert({
  entry,
  error,
  onDismiss
}: {
  readonly entry: FeedbackEntry;
  readonly error: string | undefined;
  readonly onDismiss: () => void;
}) {
  return entry.source === 'qa' ? (
    <QaFeedbackAlert item={entry.item} error={error} onDismiss={onDismiss} />
  ) : (
    <QcFeedbackAlert item={entry.item} error={error} onDismiss={onDismiss} />
  );
}

// A QC rejection: held before delivery, so there is no corrected classification - QC's verdict is
// the rejection itself, with the reviewer's reason under it. Laid out like the QA card beside it.
function QcFeedbackAlert({
  item,
  error,
  onDismiss
}: {
  readonly item: RejectedSubmission;
  readonly error: string | undefined;
  readonly onDismiss: () => void;
}) {
  const title = item.title ?? item.item_external_id ?? item.id;

  return (
    <Alert>
      <MessageSquareWarning />
      <AlertTitle className="flex flex-wrap items-center gap-1.5">
        <span className="line-clamp-1 min-w-0">{title}</span>
        <Badge variant="outline">QC rejected</Badge>
        <Badge variant="outline">{item.policy_violation}</Badge>
        <Badge variant={riskVariant(item.original_risk)}>{item.original_risk}</Badge>
      </AlertTitle>
      <AlertDescription className="grid gap-1.5">
        <div className="flex items-start gap-2">
          <div className="group bg-card relative flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden">
            {item.thumbnail_url ? (
              <SensitiveMedia
                compact
                src={item.thumbnail_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <Film className="text-muted-foreground size-4" aria-hidden="true" />
            )}
          </div>
          <ItemGroup className="min-w-0 flex-1 flex-row gap-3">
            <Item size="xs" className="px-0">
              <ItemContent>
                <ItemTitle>You said</ItemTitle>
                <ItemDescription className="text-foreground">
                  {item.original_policy_violation} / {item.original_risk}
                </ItemDescription>
                {item.original_notes ? (
                  <ItemDescription className="line-clamp-2">{item.original_notes}</ItemDescription>
                ) : null}
              </ItemContent>
            </Item>
            <Item size="xs" className="px-0">
              <ItemContent>
                <ItemTitle>QC says</ItemTitle>
                <ItemDescription className="text-foreground">Rejected</ItemDescription>
              </ItemContent>
            </Item>
          </ItemGroup>
        </div>
        <p className="text-foreground">{item.reviewer_notes ?? 'No additional notes were left.'}</p>
        <p>
          Reviewed by <span className="text-foreground font-medium">{item.reviewed_by ?? '—'}</span>{' '}
          · {formatDistanceToNow(new Date(item.reviewed_at), {addSuffix: true})}
        </p>
        {error ? <p className="text-destructive-ink font-medium">{error}</p> : null}
      </AlertDescription>
      <AlertAction className="flex items-center gap-1">
        {item.url ? <CopyUrlButton url={item.url} /> : null}
        <Button
          type="button"
          variant="outline"
          size="icon-xs"
          aria-label="Dismiss feedback"
          onClick={onDismiss}>
          <X />
        </Button>
      </AlertAction>
    </Alert>
  );
}

function QaFeedbackAlert({
  item,
  error,
  onDismiss
}: {
  readonly item: QaReviewItem;
  readonly error: string | undefined;
  readonly onDismiss: () => void;
}) {
  const title = item.title ?? item.item_external_id ?? item.id;
  const hasCorrection = item.qa_no_risk || (item.qa_policy_violation && item.qa_risk);

  return (
    <Alert>
      <MessageSquareWarning />
      <AlertTitle className="flex flex-wrap items-center gap-1.5">
        <span className="line-clamp-1 min-w-0">{title}</span>
        <Badge variant="outline">QA false positive</Badge>
        <Badge variant="outline">{item.policy_violation}</Badge>
        <Badge variant={riskVariant(item.original_risk)}>{item.original_risk}</Badge>
      </AlertTitle>
      <AlertDescription className="grid gap-1.5">
        <div className="flex items-start gap-2">
          <div className="group bg-card relative flex h-9 w-14 shrink-0 items-center justify-center overflow-hidden">
            {item.thumbnail_url ? (
              <SensitiveMedia
                compact
                src={item.thumbnail_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <Film className="text-muted-foreground size-4" aria-hidden="true" />
            )}
          </div>
          {hasCorrection ? (
            <ItemGroup className="min-w-0 flex-1 flex-row gap-3">
              <Item size="xs" className="px-0">
                <ItemContent>
                  <ItemTitle>You said</ItemTitle>
                  <ItemDescription className="text-foreground">
                    {item.original_policy_violation} / {item.original_risk}
                  </ItemDescription>
                </ItemContent>
              </Item>
              <Item size="xs" className="px-0">
                <ItemContent>
                  <ItemTitle>QA says</ItemTitle>
                  <ItemDescription className="text-foreground">
                    {item.qa_no_risk ? 'No Risk' : `${item.qa_policy_violation} / ${item.qa_risk}`}
                  </ItemDescription>
                </ItemContent>
              </Item>
            </ItemGroup>
          ) : null}
        </div>
        <p className="text-foreground">{item.review_notes ?? 'No additional notes were left.'}</p>
        <p>
          Reviewed by <span className="text-foreground font-medium">{item.reviewed_by ?? '—'}</span>
          {item.reviewed_at ? (
            <> · {formatDistanceToNow(new Date(item.reviewed_at), {addSuffix: true})}</>
          ) : null}
        </p>
        {error ? <p className="text-destructive-ink font-medium">{error}</p> : null}
      </AlertDescription>
      <AlertAction className="flex items-center gap-1">
        {item.url ? <CopyUrlButton url={item.url} /> : null}
        <Button
          type="button"
          variant="outline"
          size="icon-xs"
          aria-label="Dismiss feedback"
          onClick={onDismiss}>
          <X />
        </Button>
      </AlertAction>
    </Alert>
  );
}
